import type { GameContext } from '../core/context';
import { distance } from '../core/math';
import type { System } from '../core/system';
import type { MapMarker } from '../ui/uiTypes';
import type { Vehicle } from '../vehicles/types';
import { Autopilot, type AutopilotState } from './autopilot';
import { BeaconPool } from './beacons';
import { EXPORT_ORDER, MISSIONS, STORY, STREET_JOB, exportOrder } from './missions';
import { MEDAL_STAMP, bestNote, type JobRecord } from './medals';
import { CAB_REFUSAL_TEXT, cabCheck, cabRide, dipToBlack, fareText, minicabFare, type CabRefusal } from './minicab';
import { CAB_FIRM } from '../activities/cab';
import { MissionRunner } from './runner';
import { streetJob } from './streetJobs';
import type { MissionDef } from './types';

/**
 * Missions system (order 530): job givers around the borough, the runner, HUD objective, minimap
 * markers, world beacons and the mission hooks. Story jobs unlock one after another and persist through
 * the save (completed ids + unlocks); Halloran's export orders repeat.
 */
export interface MissionService {
  readonly active: string | null;
  /** The running job's current stage kind ('escapeHeat' while a job says "lose the law"), else null. */
  readonly stageKind: string | null;
  start(id: string, force?: boolean): boolean;
  available(id: string): boolean;
  /** All givers and their state (UI/tests). */
  list(): { id: string; title: string; available: boolean; completed: boolean }[];
  /**
   * The same jobs with what a "what next" tracker or a job list needs (src/onboarding, src/book): who
   * gives them, where (the giver's POI and position), the pitch, and what they wait for. Story order.
   */
  board(): JobBoardEntry[];
  /**
   * Online play (called only by src/net): the story jobs are single-player content (local rewards), so
   * givers shut and `start` refuses while locked. Online jobs come from the server.
   */
  setLocked(locked: boolean): void;
  /**
   * Side activities (src/activities): a source of extra job definitions (`MissionDef.activity`), asked
   * whenever the givers are listed, so it can offer a different tier each time. Register during `init`
   * (the giver interactables are made from every source's POIs at start).
   */
  registerActivities(source: () => MissionDef[]): void;
  /**
   * Play a finished story job again from the book (docs/MISSIONS.md §Replays): bonus-only pay, no unlocks,
   * the stamp and best time recorded. Null when it started, otherwise why not (plain text for a toast).
   */
  replay(id: string): string | null;
  /** Why a replay can't start right now (null = it can). */
  replayBlocked(id: string): string | null;
  /**
   * Ring for a minicab to a job's giver (the book's JOBS page). Null when the ride is on (the fare is paid
   * and the dip to black has begun), otherwise why not.
   */
  minicabTo(id: string): string | null;
  /** Would a minicab to this job's giver be offered right now (far enough, not wanted, can pay)? */
  minicabOffered(id: string): boolean;
}

export interface JobBoardEntry {
  id: string;
  title: string;
  client: string;
  tag: string;
  blurb: string;
  reward: number;
  /**
   * 'story' = one-off job in the story chain, 'export' = Halloran's rotating order, 'street' = other repeatables,
   * 'activity' = a side activity between jobs (street races, minicab shifts).
   */
  kind: 'story' | 'export' | 'street' | 'activity';
  /** Activities: what it is ("Street race · tier 1 of 3") and the best score so far. */
  kicker?: string;
  meta?: string;
  available: boolean;
  completed: boolean;
  /** Job ids still to finish before this one opens. */
  waitsFor: string[];
  /** Story jobs: the best stamp and time so far (a job finished before stamps existed reads as DONE, no time). */
  record?: JobRecord;
  giver: { poi: string; name: string; glyph: string; prompt: string; x: number; z: number; phone: boolean };
}

declare module '../core/services' {
  interface ServiceMap {
    missions: MissionService;
  }
}

declare module '../core/hooks' {
  interface BhHooks {
    /** Skip the current stage (or the intro). */
    skipStage(): void;
    /** Current mission state as JSON. */
    getMission(): Record<string, unknown>;
    /** Every job and whether it's available. */
    missionList(): { id: string; title: string; available: boolean; completed: boolean }[];
    /** Retry the last failed job from its checkpoint. */
    retryMission(): void;
    /** Fail the active mission (tests). */
    failMission(reason?: string): void;
    /** Test autopilot: drive the current car along the roads to (x, z) using normal controls. */
    autoDrive(x: number, z: number): void;
    /** Test autopilot: walk in a straight line to (x, z). */
    autoWalk(x: number, z: number): void;
    autoStop(): void;
    /** Test autopilot getaway: keep driving away from the police (alleys preferred) until autoStop. */
    autoFlee(): void;
    autoState(): AutopilotState;
    /** Test autopilot: cruise speed (m/s) when nobody's after us (default 12; races want more). */
    autoCruise(calm: number): void;
    /** Test autopilot: drive along these points (e.g. a race line) to the last one. */
    autoFollow(points: { x: number; z: number; cap?: number }[]): void;
    /** Where the running stage points (the objective marker), or null. */
    missionTarget(): { x: number; z: number } | null;
    /** Position of a layout POI by id (test convenience). */
    poiPos(id: string): { x: number; z: number } | null;
    /** Id of the nearest empty, drivable civilian car (-1 if none within 80 m). */
    nearestCar(x: number, z: number): number;
    /** Position/health of any vehicle by id (test convenience; snapshot lists are capped). */
    vehicleInfo(id: number): { x: number; z: number; door: { x: number; z: number }; spec: string; health: number; destroyed: boolean } | null;
  }
}

export function createMissionSystem(): System {
  let ctx: GameContext;
  let runner: MissionRunner;
  let autopilot: Autopilot;
  let beacons: BeaconPool | null = null;
  const offs: (() => void)[] = [];
  /** Failure screen waiting for the player's choice — givers stay shut meanwhile. */
  let awaitingChoice = false;
  /** A retry/restart picked while the police still had the player (NICKED/WASTED); runs on respawn. */
  let deferredChoice: { def: MissionDef; choice: string | undefined } | null = null;
  let locked = false;

  /**
   * NICKED / WASTED. During the outro the last objective is already done (the player only watches, hands off the
   * wheel): the job is paid, not failed — before, an arrest in those up-to-20 s took the whole job back.
   */
  function blown(reason: string): void {
    if (runner.status === 'outro') runner.skipStage();
    else runner.fail(reason, true);
  }

  function runChoice(def: MissionDef, choice: string | undefined): void {
    awaitingChoice = false;
    deferredChoice = null;
    if (runner.status !== 'failed' || runner.def !== def) return;
    if (choice === 'retry') runner.retry();
    else if (choice === 'restart') runner.start(def, { replay: runner.replay });
    else if (choice === 'cab') cabToStart(def);
    // Walk away: the job's cars go back to the street (before, they kept their 'mission' tag for good, so the
    // abandoned-car cull never took them and their wrecks stood wherever the job was blown).
    else if (choice === 'quit') runner.abort();
  }

  // ---------------------------------------------------------------- minicab (docs/MISSIONS.md §Minicab)

  function cabSituation(to: { x: number; z: number }, onJob: boolean): Parameters<typeof cabCheck>[0] {
    const p = ctx.services.get('player').pos;
    return {
      heat: ctx.state.heat,
      online: locked || !!ctx.services.tryGet('net')?.online,
      onJob,
      money: ctx.services.tryGet('economy')?.balance ?? ctx.state.money,
      distance: Math.hypot(to.x - p.x, to.z - p.z),
      fare: minicabFare(ctx),
    };
  }

  /** Take the fare and ride (after the dip); `then` runs once the player is in the car. */
  function ride(to: { x: number; z: number }, then?: () => void): boolean {
    const eco = ctx.services.tryGet('economy');
    const fare = minicabFare(ctx);
    if (eco && fare > 0 && !eco.spend(fare, 'minicab')) return false;
    dipToBlack(ctx, `${CAB_FIRM} · ${fareText(fare)}`, () => {
      cabRide(ctx, to);
      then?.();
    });
    return true;
  }

  /**
   * "Minicab back to the start" on a blown job's sheet: the job starts again from the top with you at the
   * kerb by its giver. Checked again now (an arrest may have moved you; the police may still be after you):
   * refused, it falls back to an ordinary restart and says why.
   */
  function cabToStart(def: MissionDef): void {
    const giver = runner.poi(def.giver.poi).pos;
    const why: CabRefusal | null = cabCheck(cabSituation(giver, false));
    const replay = runner.replay;
    if (why) {
      if (why !== 'near') ui()?.toast(CAB_REFUSAL_TEXT[why], 'warn', 4);
      runner.start(def, { replay });
      return;
    }
    // Givers stay shut through the dip; the job starts once you're at the kerb.
    awaitingChoice = true;
    const rode = ride(giver, () => {
      awaitingChoice = false;
      if (runner.status === 'failed' && runner.def === def) runner.start(def, { replay });
    });
    if (!rode) {
      awaitingChoice = false;
      runner.start(def, { replay });
    }
  }

  /** The cab line on a blown job's sheet, when the ride would be worth it (null: not offered). */
  function cabAction(def: MissionDef, silent: boolean): { id: string; label: string } | null {
    const giver = runner.poi(def.giver.poi).pos;
    const s = cabSituation(giver, false);
    // NICKED / WASTED: the cells or the infirmary clear the wanted level and are rarely near the job.
    const why = cabCheck(silent ? { ...s, heat: 0, distance: Infinity } : s);
    return why ? null : { id: 'cab', label: `Minicab back to the start — ${fareText(minicabFare(ctx))}` };
  }

  // ---------------------------------------------------------------- replays (docs/MISSIONS.md §Replays)

  function replayBlocked(id: string): string | null {
    if (locked || ctx.services.tryGet('net')?.online) return 'Replays are for offline play.';
    if (!STORY.includes(id) || !completed(id)) return 'Finish the job first.';
    if (runner.active || awaitingChoice || deferredChoice) return 'Finish what you’re on first.';
    if (ctx.state.heat > 0) return 'Lose the police first.';
    return null;
  }
  /** Halloran's current order (rotates after each delivery). */
  let exportDef: MissionDef;
  /** Lenny's current street job at the Coalgate phone box (rotates after each one). */
  let streetDef: MissionDef;
  const exported = new Set<number>();
  const activitySources: (() => MissionDef[])[] = [];
  const giverDefs = (): MissionDef[] => {
    const defs = [...MISSIONS, exportDef, streetDef];
    for (const src of activitySources) defs.push(...src());
    return defs;
  };
  /** The mood the timeOfDay service was last asked to hold for a job (null = none). */
  let forcedMood: MissionDef['mood'] | null = null;

  const save = () => ctx.services.get('save');
  const ui = () => ctx.services.tryGet('ui');

  function completed(id: string): boolean {
    return save().isMissionComplete(id);
  }

  function available(def: MissionDef): boolean {
    if (def.requires.some((r) => !completed(r))) return false;
    if (def.requiresUnlock && !save().hasUnlock(def.requiresUnlock)) return false;
    return def.repeatable || !completed(def.id);
  }

  function defById(id: string): MissionDef | undefined {
    return giverDefs().find((d) => d.id === id);
  }

  function start(id: string, force = false): boolean {
    if (locked) return false;
    const def = defById(id);
    if (!def) {
      console.warn(`[missions] unknown mission '${id}'`);
      return false;
    }
    if (!force && !available(def)) return false;
    if (runner.active) runner.abort();
    awaitingChoice = false;
    runner.start(def);
    return true;
  }

  function nextExport(): void {
    exportDef = exportOrder(save().data.stats.exportsDone);
    ui()?.registerMission(exportDef.id, { title: exportDef.title, client: exportDef.client, selfPresented: true });
  }

  function nextStreetJob(): void {
    streetDef = streetJob(save().data.stats.streetJobsDone);
    ui()?.registerMission(streetDef.id, { title: streetDef.title, client: streetDef.client, selfPresented: true });
  }

  const service: MissionService = {
    get active() {
      return runner.active ? runner.def?.id ?? null : null;
    },
    get stageKind() {
      return runner.active && runner.status === 'running' ? runner.stage?.kind ?? null : null;
    },
    start,
    available: (id) => {
      const d = defById(id);
      return !!d && available(d);
    },
    list: () => giverDefs().map((d) => ({ id: d.id, title: d.title, available: available(d), completed: completed(d.id) })),
    board: () =>
      giverDefs().map((d) => {
        const poi = runner.poi(d.giver.poi);
        return {
          id: d.id,
          title: d.title,
          client: d.client,
          tag: d.tag,
          blurb: d.blurb,
          reward: d.reward,
          kind: d.activity ? 'activity' : d.id === EXPORT_ORDER ? 'export' : d.repeatable ? 'street' : 'story',
          ...(d.activity ? { kicker: d.activity.kicker } : {}),
          ...(d.activity?.meta?.() ? { meta: d.activity.meta() } : {}),
          available: !locked && available(d),
          // An activity counts as done once it has been played at all (a lost race is still a race).
          completed: d.activity?.tried ? d.activity.tried() : completed(d.id),
          waitsFor: d.requires.filter((r) => !completed(r)),
          ...(STORY.includes(d.id) && completed(d.id) ? { record: save().data.jobRecords[d.id] ?? { medal: 1, best: null, runs: 1 } } : {}),
          giver: { poi: d.giver.poi, name: poi.name, glyph: d.giver.glyph, prompt: d.giver.prompt, x: poi.pos.x, z: poi.pos.z, phone: poi.kind === 'phonebox' },
        };
      }),
    setLocked(on) {
      locked = on;
      if (on && runner.active) runner.fail('You went online, so the job was stopped. Play it again offline.', true);
    },
    registerActivities(source) {
      activitySources.push(source);
    },
    replayBlocked,
    replay(id) {
      const why = replayBlocked(id);
      if (why) return why;
      const def = MISSIONS.find((d) => d.id === id)!;
      runner.start(def, { replay: true });
      return null;
    },
    minicabOffered(id) {
      const def = defById(id);
      if (!def || runner.active || awaitingChoice) return false;
      return cabCheck(cabSituation(runner.poi(def.giver.poi).pos, false)) === null;
    },
    minicabTo(id) {
      const def = defById(id);
      if (!def) return 'No such job.';
      const why = cabCheck(cabSituation(runner.poi(def.giver.poi).pos, runner.active || awaitingChoice));
      if (why) return CAB_REFUSAL_TEXT[why];
      return ride(runner.poi(def.giver.poi).pos) ? null : CAB_REFUSAL_TEXT.broke;
    },
  };

  function markerSource(out: MapMarker[]): void {
    if (ctx.state.mode !== 'playing' && ctx.state.mode !== 'paused') return;
    if (runner.active) {
      const h = runner.hud();
      if (h.target) out.push({ x: h.target.x, z: h.target.z, kind: 'objective', pulse: true, pin: true });
      for (const p of h.vehicleMarkers) out.push({ x: p.x, z: p.z, kind: 'target', pin: true });
      return;
    }
    if (locked) return;
    for (const def of giverDefs()) {
      if (!available(def)) continue;
      const p = runner.poi(def.giver.poi).pos;
      out.push({ x: p.x, z: p.z, kind: 'friend', glyph: def.giver.glyph, pin: false });
    }
  }

  return {
    id: 'missions',
    order: 530,
    init(c) {
      ctx = c;
      runner = new MissionRunner(c, {
        onStarted(def) {
          const u = ui();
          if (!u) return;
          u.registerMission(def.id, { title: def.title, client: def.client, selfPresented: true });
          u.letterbox(true);
          void u.showMissionIntro({ id: def.id, title: def.title, client: def.client, blurb: def.blurb, reward: def.reward, tag: def.tag });
        },
        onCompleted(def, lines, _total, run) {
          const u = ui();
          // Story jobs earn a stamp; the save keeps the best one and the best time.
          const rec = run && run.medal > 0 ? save().recordJobRun(def.id, run.medal, run.time) : null;
          if (def.id === EXPORT_ORDER) {
            const stats = save().data.stats;
            stats.exportsDone = (stats.exportsDone) + 1;
            save().markDirty();
            const id = runner.snapshot().vars as Record<string, unknown>;
            if (typeof id.exportedId === 'number') exported.add(id.exportedId);
            nextExport();
          }
          if (def.id === STREET_JOB) {
            save().data.stats.streetJobsDone += 1;
            save().markDirty();
            nextStreetJob();
          }
          if (!u) return;
          u.setObjective(null);
          u.letterbox(false);
          const subtitle = def.resultLine ? def.resultLine(runner.script) : `${def.client} is pleased. Nobody saw a thing.`;
          const stamp = typeof def.resultStamp === 'function' ? def.resultStamp(runner.script) : def.resultStamp;
          // A replay opens nothing: say so where "Next job: …" would go (and keep the UI from filling that in).
          const after = run?.replay ? { after: 'A replay pays the bonus only and moves nothing on. Your stamps are in the book: JOBS → Done.' } : {};
          const medal = run && rec && run.medal > 0 ? { tier: run.medal as 1 | 2 | 3, stamp: MEDAL_STAMP[run.medal as 1 | 2 | 3], note: bestNote(run.medal, run.time, rec, !run.replay && rec.record.runs === 1) } : null;
          void u.showResult({ kind: 'success', title: def.title, subtitle, lines, ...(stamp ? { stamp } : {}), ...(medal ? { medal } : {}), ...after });
          const next = MISSIONS.find((d) => available(d));
          if (next && next.id !== def.id && !run?.replay) {
            const giver = runner.poi(next.giver.poi);
            u.toast(`New job: “${next.title}” — ${giver.name}`, 'good', 5);
          }
        },
        onFailed(def, reason, canRetry, silent) {
          const u = ui();
          if (!u) return;
          u.setObjective(null);
          u.letterbox(false);
          // Silent = NICKED/WASTED: their own sheet shows first (the UI queues results), then this one.
          awaitingChoice = true;
          const cab = cabAction(def, silent);
          const actions = [
            ...(canRetry ? [{ id: 'retry', label: 'Retry from checkpoint' }] : []),
            ...(cab ? [cab] : []),
            { id: 'restart', label: 'Start the job again' },
            { id: 'quit', label: 'Walk away' },
          ];
          const subtitle = silent ? `${reason} You can try the job again.` : reason;
          void u.showResult({ kind: 'failed', title: def.title, subtitle, actions, ...(def.failStamp && !silent ? { stamp: def.failStamp } : {}) }).then((choice) => {
            // Still in the cells / on the stretcher: wait for the police module to put us back on the street.
            if (ctx.services.tryGet('economy')?.outcomePending) deferredChoice = { def, choice };
            else runChoice(def, choice);
          });
        },
      });
      c.services.provide('missions', service);
      autopilot = new Autopilot(c);

      offs.push(
        c.events.on('playerBusted', () => blown('Nicked — the police arrested you. DS Holloway sends his regards.')),
        c.events.on('playerWasted', () => blown('Wasted — you were knocked down and taken to hospital.')),
        c.events.on('playerRespawned', () => {
          const d = deferredChoice;
          if (d) queueMicrotask(() => runChoice(d.def, d.choice));
        }),
        c.events.on('newGameStarted', () => {
          runner.abort();
          nextExport();
          nextStreetJob();
        }),
      );

      c.hooks.register('startMission', (id) => {
        start(id, true);
      });
      c.hooks.register('skipStage', () => runner.skipStage());
      c.hooks.register('getMission', () => runner.snapshot());
      c.hooks.register('missionList', () => service.list());
      c.hooks.register('retryMission', () => {
        if (runner.status === 'failed') runner.retry();
      });
      c.hooks.register('failMission', (reason) => runner.fail(reason ?? 'Debug failure'));
      c.hooks.register('autoDrive', (x, z) => autopilot.driveTo(x, z));
      c.hooks.register('autoWalk', (x, z) => autopilot.walkTo(x, z));
      c.hooks.register('autoStop', () => autopilot.stop());
      c.hooks.register('autoFlee', () => autopilot.flee());
      c.hooks.register('autoState', () => autopilot.state());
      c.hooks.register('autoFollow', (points) => autopilot.follow(points));
      c.hooks.register('missionTarget', () => {
        const t = runner.hud().target;
        return t ? { x: +t.x.toFixed(2), z: +t.z.toFixed(2) } : null;
      });
      c.hooks.register('autoCruise', (calm) => {
        autopilot.cruise = Math.max(4, Math.min(40, calm));
      });
      c.hooks.register('poiPos', (id) => {
        const p = c.services.get('layout').pois.find((x) => x.id === id);
        return p ? { x: p.pos.x, z: p.pos.z } : null;
      });
      c.hooks.register('vehicleInfo', (id) => {
        const v = c.services.get('vehicles').get(id);
        if (!v) return null;
        const r = v.body.right();
        const off = v.spec.width / 2 + 1.1;
        return {
          x: v.pos.x,
          z: v.pos.z,
          door: { x: v.pos.x + r.x * off, z: v.pos.z + r.z * off },
          spec: v.spec.id,
          health: +(v.health / v.spec.maxHealth).toFixed(2),
          destroyed: v.destroyed,
        };
      });
      c.hooks.register('nearestCar', (x, z) => {
        // A civilian car: not police, not yours, and online nobody's (a player's own car or Halloran's loaner).
        const net = c.services.tryGet('net');
        const owned = (q: Vehicle): boolean => q.ownedByPlayer || (!!net?.online && (net.vehicleInfo(q)?.ownerCid ?? null) !== null);
        const v = c.services.get('vehicles').nearest({ x, z }, 80, (q) => !q.isPolice && !q.destroyed && q.spec.id !== 'lorry' && q.occupant === null && !owned(q));
        return v ? v.id : -1;
      });
      c.hooks.registerSnapshot('missions', () => ({ ...runner.snapshot(), list: service.list() }));
    },
    start() {
      const u = ui();
      nextExport();
      nextStreetJob();
      for (const def of MISSIONS) u?.registerMission(def.id, { title: def.title, client: def.client, selfPresented: true });
      u?.addMarkerSource(markerSource);
      if (ctx.render) beacons = new BeaconPool(ctx.scene);

      const interactions = ctx.services.get('interactions');
      // Job givers: one interactable per giver location; it offers whichever job is available there.
      const giverPois = [...new Set(giverDefs().map((d) => d.giver.poi))];
      for (const poiId of giverPois) {
        const poi = runner.poi(poiId);
        const here = (): MissionDef | undefined => giverDefs().find((d) => d.giver.poi === poiId && available(d));
        const whileDriving = giverDefs().some((d) => d.giver.poi === poiId && d.giver.whileDriving);
        offs.push(
          interactions.register({
            id: `job:${poiId}`,
            pos: () => poi.pos,
            radius: Math.max(whileDriving ? 7 : 3.5, poi.radius + 2.5),
            priority: 2,
            whileDriving,
            // From the driver's seat only once you've pulled up (driving past, E still means "get out").
            enabled: () => ctx.state.mode === 'playing' && !locked && !runner.active && !awaitingChoice && !!here() && (!whileDriving || (ctx.services.get('player').vehicle?.speed ?? 0) < 3),
            prompt: () => {
              const d = here()!;
              return `${d.giver.prompt} — “${d.title}”`;
            },
            onInteract: () => {
              const d = here();
              if (d) start(d.id);
            },
          }),
        );
      }
      // Hold-to-act stages: this beats the vehicle prompt (priority 10) so E doesn't hop into the van.
      offs.push(
        interactions.register({
          id: 'job:hold',
          pos: () => runner.hud().target ?? { x: 1e9, z: 1e9 },
          radius: 8,
          priority: 1,
          whileDriving: true,
          enabled: () => runner.status === 'running' && !!runner.hud().hold?.available,
          prompt: () => `Hold — ${runner.hud().hold?.prompt ?? ''}`,
          onInteract: () => undefined,
        }),
      );
    },
    fixedUpdate(dt) {
      runner.fixedUpdate(dt);
      autopilot.fixedUpdate(dt);
      // Exported cars vanish onto the boat once the player has walked off.
      if (exported.size) {
        const vehicles = ctx.services.get('vehicles');
        const player = ctx.services.get('player');
        for (const id of exported) {
          const v = vehicles.get(id);
          if (!v) exported.delete(id);
          else if (player.vehicle !== v && distance(v.pos, player.pos) > 25) {
            vehicles.despawn(v);
            exported.delete(id);
          }
        }
      }
    },
    update(dt) {
      // A job may hold a mood (Last Orders is a night job) — through the result sheet too, until the
      // player retries or walks away; then Settings → Time of day takes over again.
      const want = runner.def && (runner.active || awaitingChoice || deferredChoice) ? runner.def.mood ?? null : null;
      if (want !== forcedMood) {
        const tod = ctx.services.tryGet('timeOfDay');
        if (tod) {
          tod.force(want);
          forcedMood = want;
        }
      }
      const u = ui();
      const h = runner.hud();
      // The outro has no objective (hud() gives none), so the last stage's clipping comes down with the
      // bars instead of standing over Mags' lines until the tally.
      if (runner.status === 'running' || runner.status === 'intro' || runner.status === 'outro') u?.setObjective(h.objective);
      if (!beacons) return;
      beacons.begin(dt);
      if (ctx.state.mode === 'playing' || ctx.state.mode === 'paused') {
        if (runner.active) {
          const stage = runner.stage;
          if (h.target && stage && (stage.kind === 'goTo' || stage.kind === 'hold')) beacons.place(h.target.x, h.target.z, Math.min(stage.radius, 9), 'objective');
          else if (h.target && stage?.kind === 'custom') beacons.place(h.target.x, h.target.z, Math.min(stage.radius ?? 4, 9), 'objective');
          for (const p of h.vehicleMarkers) beacons.place(p.x, p.z, 2.8, 'vehicle');
        } else if (!awaitingChoice) {
          for (const def of giverDefs()) {
            if (!available(def)) continue;
            const p = runner.poi(def.giver.poi).pos;
            beacons.place(p.x, p.z, 2.2, 'giver');
          }
        }
      }
      beacons.end();
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      beacons?.dispose();
      if (forcedMood) ctx.services.tryGet('timeOfDay')?.force(null);
    },
  };
}
